import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance.js";
import Pagination from "../../common/Pagination/index.js";
import Loader from "../../common/Loader/index.js";
import { BiSolidEditAlt } from "react-icons/bi";
import { MdClose, MdDeleteForever } from "react-icons/md";
import Model from "../../common/Model/index.js";
import { LiaSearchPlusSolid } from "react-icons/lia";
import { notify, Toast } from "../../common/Toast/index.js";
import FormFields from "../../common/FormFields/index.js";
import { getUserMenuPermission } from "../../common/Helper/index.js";

const Item = () => {
  const permission = getUserMenuPermission(4);
  const [isItemData, setIsItemData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [isItemType, setIsItemType] = useState([]);
  const [isCoilData, setIsCoilData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    item_Code: 0,
    item_Name: "",
    description: "",
    item_Rate: "",
    item_Type: "",
    item_Face: "",
    coil_Used: "",
    thickness: "",
    image: null,
  });
  const userId = localStorage.getItem("userId");

  const fetchItemType = async () => {
    if (isItemType?.length > 0) return;
    try {
      const res = await axiosInstance.get(`Item/GetAllItemType`);
      setIsItemType(res.data);
    } catch (error) {
      console.error("Error fetching GetAllItemType:", error);
    }
  };
  const fetchCoilData = async () => {
    if (isCoilData?.length > 0) return;
    try {
      const res = await axiosInstance.get(`Item/GetCoilMaster`);
      setIsCoilData(res.data);
    } catch (error) {
      console.error("Error fetching GetCoilMaster:", error);
    }
  };

  const fetchItemData = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.post(
        `Item/GetAll/?pageNumber=${currentPage}&pageSize=${pageSize}`,
        {
          pageNumber: currentPage,
          pageSize,
          item_Code: 0,
        }
      );
      setIsItemData(res.data.items);
      setFilteredData(res.data.items);
      setTotalRecords(res.data.totalRecords);
      setTotalPages(Math.ceil(res.data.totalRecords / pageSize));
    } catch (err) {
      notify("Error fetching data:", "error");
    } finally {
      setLoading(false);
    }
  };
  const fields = [
    {
      name: "item_Name",
      label: "Item Name",
      type: "text",
      required: true,
      errorMessage: "Item name is required",
    },
    { name: "description", label: "Description", type: "text" },
    {
      name: "item_Rate",
      label: "Item Rate",
      type: "number",
      required: true,
      min: 0,
      step: "any",
      errorMessage: "Positive value required",
    },
    { name: "item_Face", label: "Item Face", type: "number" },
    {
      name: "item_Type",
      label: "Item Type",
      type: "select",
      id: "item_Type",
      options: isItemType.map((type) => ({
        value: type.item_Type.toString(),
        label: type.item_Type_Name,
      })),
    },
    {
      name: "coil_Used",
      label: "Coil Used",
      type: "select",
      id: "coil_Used",
      options: isCoilData.map((coil) => ({
        value: coil.coil_Used.toString(),
        label: coil.coil_Name,
      })),
    },
    { name: "thickness", label: "Thickness", type: "number" },
    { name: "image", label: "Upload Image", type: "file" },
  ];

  const handleInputChange = (e) => {
    const { name, type, files, value } = e.target;

    if (type === "file") {
      const file = files[0];
      setFormData((prev) => ({
        ...prev,
        [name]: file,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleAddItem = () => {
    if (!permission.isInsert) {
      notify("You don't have permission to add Item.", "error");
      return;
    }
    setFormData({
      item_Code: "",
      item_Name: "",
      description: "",
      item_Rate: "",
      item_Type: "",
      item_Face: "",
      coil_Used: "",
      thickness: "",
    });
    setIsEditing(false);
    setIsModelOpen(true);
  };

  const handleEditItem = async (id) => {
    try {
      const { data } = await axiosInstance.get(`Item/GetById/${id}`);
      if (data) {
        setFormData({
          item_Code: data.item_Code || "",
          item_Name: data.item_Name || "",
          description: data.description || "",
          item_Rate: data.item_Rate || "",
          item_Type: data.item_Type || "",
          item_Face: data.item_Face || "",
          coil_Used: data.coil_Used || "",
          thickness: data.thickness || "",
          image: data.image || "",
        });
        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (err) {
      notify("Error fetching Item data:", err);
    }
  };

  const handleDeleteItem = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(`Item/Delete/${selectedId}`);
      fetchItemData();
      notify("Item deleted successfully!", "error");
    } catch (err) {
      notify("Failed to delete Item!", "error");
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing && !permission.isInsert) return;
    if (isEditing && !permission.isUpdate) return;
    const formDataToSend = new FormData();
    formDataToSend.append("Item_Code", formData.item_Code || 0);
    formDataToSend.append("Item_Name", formData.item_Name);
    formDataToSend.append("Description", formData.description);
    formDataToSend.append("Item_Rate", formData.item_Rate);
    formDataToSend.append("Item_Type", parseInt(formData.item_Type) || 0);
    formDataToSend.append("Item_Face", Number(formData.item_Face));
    formDataToSend.append("Coil_Used", parseInt(formData.coil_Used) || 0);
    formDataToSend.append("Thickness", Number(formData.thickness));

    if (formData.image) {
      formDataToSend.append("image", formData.image);
    }
    if (isEditing) {
      formDataToSend.append("Updated_By", userId);
    } else {
      formDataToSend.append("Created_By", userId);
    }
    try {
      await axiosInstance.post("Item/SaveUpdate", formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      fetchItemData();
      setIsModelOpen(false);
      notify(
        isEditing ? "Item updated successfully!" : "Item added successfully!",
        "success"
      );
      setFormData({
        item_Code: "",
        item_Name: "",
        description: "",
        item_Rate: "",
        item_Type: "",
        item_Face: "",
        coil_Used: "",
        thickness: "",
        image: null,
      });

      document.getElementById("fileInput").value = "";
    } catch (err) {
      console.error("Error submitting form:", err);
    }
  };

  const fetchFilteredItems = async (query) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `Item/FilterByName?itemName=${query}`
      );
      setFilteredData(res.data);
      setTotalRecords(res.data.length);
    } catch (err) {
      notify("Error fetching filtered data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItemData();
    fetchItemType();
    fetchCoilData();
  }, [currentPage]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      fetchItemData();
    } else {
      fetchFilteredItems(searchQuery);
    }
  }, [searchQuery, currentPage]);

  const clearSearch = () => {
    setSearchQuery("");
    fetchItemData();
  };

  return (
    <>
      <Toast />
      <div >
        <div className="rounded-3xl bg-transparent px-2 pt-2 pb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 tracking-tight">Main Item Catalogue</h2>
              <p className="text-sm text-gray-500 font-medium">Manage product specifications and pricing</p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
              <div className="relative w-full sm:w-[300px]">
                <input
                  type="text"
                  className="w-full bg-white border border-gray-100 py-2.5 pl-10 pr-10 rounded-xl focus:ring-2 focus:ring-primary/10 focus:border-primary outline-none text-sm shadow-sm transition-all"
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary/60">
                  <LiaSearchPlusSolid className="w-5 h-5" />
                </button>
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-400 hover:text-red-500 transition-colors"
                  >
                    <MdClose className="w-5 h-5" />
                  </button>
                )}
              </div>
              {permission.isInsert && (
                <button
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-primary hover:bg-primary/95 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg shadow-primary/20 transition-all active:scale-95"
                  onClick={handleAddItem}
                >
                  <span>+</span> Add Item
                </button>
              )}
            </div>
          </div>

          <div className="premium-card overflow-hidden border-none shadow-xl shadow-gray-200/50">
            {loading ? (
              <div className="p-20 flex justify-center"><Loader /></div>
            ) : permission.isView ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-[#f9fafc] text-[#405189] uppercase tracking-wider text-[10px] font-bold border-b border-gray-100">
                    <tr>
                      <th className="px-6 py-4">Image</th>
                      <th className="px-6 py-4">Item Name</th>
                      <th className="px-6 py-4">Type</th>
                      <th className="px-6 py-4 text-center">Rate</th>
                      <th className="px-6 py-4 text-center">Face</th>
                      <th className="px-6 py-4">Coil</th>
                      <th className="px-6 py-4 text-center">Thk</th>
                      <th className="px-6 py-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 bg-white">
                    {filteredData?.length > 0 ? (
                      filteredData.map((item) => (
                        <tr
                          key={item.item_Code}
                          className="hover:bg-blue-50/30 transition-colors group cursor-pointer"
                        >
                          <td className="px-6 py-4">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt={item.item_Name}
                                className="w-12 h-10 object-cover rounded-lg shadow-sm border border-gray-100"
                              />
                            ) : (
                              <div className="w-12 h-10 bg-gray-50 rounded-lg flex items-center justify-center text-[10px] text-gray-400 font-bold border border-dashed border-gray-200">
                                NO IMG
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="font-bold text-gray-800">{item.item_Name}</span>
                              <span className="text-[10px] text-gray-500 italic truncate max-w-[150px]">
                                {item.description || "No description"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-bold uppercase">
                              {isItemType.find((type) => type.item_Type === item.item_Type)?.item_Type_Name || "-"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center font-bold text-primary">
                            ₹{Number(item.item_Rate).toLocaleString("en-IN")}
                          </td>
                          <td className="px-6 py-4 text-center text-gray-600 font-medium">
                            {item.item_Face || "0"}
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-xs text-gray-600 font-medium">
                              {isCoilData.find((coil) => coil.coil_Used === item.coil_Used)?.coil_Name || "-"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-md font-bold text-[10px]">
                              {item.thickness}mm
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              {permission.isUpdate && (
                                <button
                                  className="p-2 bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors"
                                  onClick={(e) => { e.stopPropagation(); handleEditItem(item.item_Code); }}
                                  title="Edit Item"
                                >
                                  <BiSolidEditAlt className="w-4 h-4" />
                                </button>
                              )}
                              {permission.isDelete && (
                                <button
                                  className="p-2 bg-gray-50 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.item_Code); }}
                                  title="Delete Item"
                                >
                                  <MdDeleteForever className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-20 text-gray-400 font-medium italic">
                          No items found matching your criteria.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-red-500 text-lg py-20 font-bold bg-white">
                Access Denied: You don't have permission to view items.
              </div>
            )}

            <div className="bg-[#f9fafc] px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-[13px] text-gray-500 font-medium">
                Showing <span className="font-bold text-gray-800">
                  {isItemData?.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to {Math.min(currentPage * pageSize, totalRecords)}
                </span> of{" "}
                <span className="font-bold text-gray-800">{totalRecords}</span> records
              </p>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </div>

      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit Item" : "Add Item"}
      >
        <div className="p-1">
          <FormFields
            onSubmit={handleSubmit}
            setIsModelOpen={setIsModelOpen}
            formData={formData}
            handleInputChange={handleInputChange}
            fields={fields}
            onClear={() => setIsModelOpen(false)}
            isEditing={isEditing}
          />
        </div>
      </Model>

      <Model
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <MdDeleteForever className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Confirm Delete</h3>
          <p className="text-gray-500 mb-8 max-w-[300px] mx-auto">
            Are you sure you want to permanently delete this item? This action cannot be undone.
          </p>
          <div className="flex justify-center gap-3">
            <button
              className="px-6 py-2.5 rounded-xl font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="px-6 py-2.5 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 shadow-lg shadow-red-200 transition-all active:scale-95"
              onClick={handleConfirmDelete}
            >
              Delete Item
            </button>
          </div>
        </div>
      </Model>
    </>
  );
};

export default Item;
